import { getAuthData, getAuthUserId } from "../auth/session.js";
import * as React from "react";
import { IconHistory, IconChevronLeft, IconChevronRight } from "../../components/icons.jsx";
import { PenaltyScene } from "./PenaltyScene.jsx";
import { PenaltyHistory } from "./PenaltyHistory.jsx";
import { PenaltyRules } from "./PenaltyRules.jsx";
import { penaltyRequest, pendingKey, readPending, clearPending, currentRound } from "./api.js";
import { KEEPERS, getKeeper, readKeeper, keeperQuote } from "./keepers.js";
import "./penalty.css";

const balanceFields = { metacoin: "metacoins", crystal: "crystals", spin: "spins", gems: "gems", event: "event" };
const resourceEmoji = (name) => (props) => <img src={`/assets/resource-emojis/${name}.webp`} className="penalty-resource-emoji" alt="" {...props} />;
const icons = { metacoin: resourceEmoji("coin"), crystal: resourceEmoji("crystal"), spin: resourceEmoji("card"), gems: resourceEmoji("ruby"), event: () => <span aria-hidden="true">🎃</span> };
const fmt = value => Number(value || 0).toLocaleString("ru-RU");
const mult = value => `×${(value / 100).toLocaleString("ru-RU", { maximumFractionDigits: 2 })}`;
const outcomes = { lost: "Сейв — ставка потеряна", cashed_out: "Выигрыш получен", maxed: "×20 — выигрыш получен автоматически" };
const goalCount = value => `${value} ${value % 10 === 1 && value % 100 !== 11 ? "гол" : value % 10 >= 2 && value % 10 <= 4 && (value % 100 < 12 || value % 100 > 14) ? "гола" : "голов"}`;

export function PenaltyPage({ onBack, onBalanceChange }) {
  const initData = getAuthData() || "";
  const userId = getAuthUserId();
  const key = pendingKey(userId);
  const guideKey = `metacard-penalty-guide-seen:${userId}`;
  const keeperStorageKey = `metacard-penalty-keeper:${userId}`;
  const [keeperId, setKeeperId] = React.useState(() => {
    try { return readKeeper(localStorage, keeperStorageKey); } catch { return KEEPERS[0].id; }
  });
  const keeper = getKeeper(keeperId);
  const keeperIndex = KEEPERS.findIndex(entry => entry.id === keeper.id);
  const [quote, setQuote] = React.useState(() => keeperQuote(keeper));
  const [data, setData] = React.useState(null);
  const [round, setRound] = React.useState(null);
  const [currency, setCurrency] = React.useState("metacoin");
  const [stake, setStake] = React.useState(10);
  const [busy, setBusy] = React.useState(true);
  const [error, setError] = React.useState("");
  const [pending, setPending] = React.useState(null);
  const [animation, setAnimation] = React.useState(null);
  const [panel, setPanel] = React.useState("game");
  const [rulesOpen, setRulesOpen] = React.useState(false);
  const guideChecked = React.useRef(false);
  const gate = React.useRef(false);
  const multiplierStrip = React.useRef(null);
  const mounted = React.useRef(true);
  const balanceCallback = React.useRef(onBalanceChange);
  balanceCallback.current = onBalanceChange;
  const active = round?.status === "active";
  const locked = busy || !!animation || !!pending || !data;
  const rule = data?.rules.currencies[currency];
  const balance = data?.balance[balanceFields[currency]] || 0;
  const back = rulesOpen ? () => setRulesOpen(false) : panel === "game" ? onBack : () => setPanel("game");
  const backButton = window.Telegram?.WebApp?.BackButton;

  React.useEffect(() => {
    const event = animation?.event || (round?.status === "lost" ? "save" : round?.goals ? "goal" : "ready");
    setQuote(previous => keeperQuote(getKeeper(keeperId), event, previous, Math.random, round?.goals || 0));
  }, [keeperId, round?.id, round?.goals, round?.status, animation?.id]);

  function changeKeeper(direction) {
    if (busy || animation || pending) return;
    const next = KEEPERS[(keeperIndex + direction + KEEPERS.length) % KEEPERS.length];
    setKeeperId(next.id);
    try { localStorage.setItem(keeperStorageKey, next.id); } catch { /* Cosmetic choice still works without storage. */ }
  }

  React.useEffect(() => {
    if (!data?.rules || guideChecked.current) return;
    guideChecked.current = true;
    try {
      if (localStorage.getItem(guideKey) === "1") return;
      localStorage.setItem(guideKey, "1");
    } catch {
      // Storage may be unavailable in private webviews; keep this visit usable.
    }
    setRulesOpen(true);
  }, [data?.rules, guideKey]);

  React.useEffect(() => {
    const strip = multiplierStrip.current;
    const current = strip?.querySelector('[aria-current="step"]');
    if (!current) return;
    const reveal = () => {
      const left = current.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
      strip.scrollTo({ left: left - (strip.clientWidth - current.offsetWidth) / 2, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    };
    reveal();
    const observer = new ResizeObserver(reveal);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [panel, round?.goals]);

  React.useEffect(() => {
    if (!backButton) return;
    backButton.show(); backButton.onClick(back);
    return () => { backButton.offClick(back); backButton.hide(); };
  }, [backButton, back]);

  React.useEffect(() => {
    if (!animation) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => setAnimation(null), reduced ? 100 : 1550);
    return () => window.clearTimeout(timer);
  }, [animation]);

  function applyState(state, lastRound) {
    if (!mounted.current) return;
    setData(state);
    const latestFinished = state.history[0];
    const restored = state.activeRound || (lastRound && (!latestFinished || Date.parse(lastRound.created_at) >= Date.parse(latestFinished.created_at)) ? lastRound : latestFinished) || null;
    setRound(restored);
    if (state.activeRound) {
      setCurrency(state.activeRound.currency);
      setStake(state.activeRound.stake);
    }
    balanceCallback.current(state.balance);
  }

  async function restore() {
    if (gate.current) return;
    gate.current = true;
    setBusy(true); setError("");
    try {
      const saved = readPending(sessionStorage, key);
      let lastRound = null;
      if (saved) {
        setPending(saved);
        try {
          const result = await penaltyRequest(saved.operation, saved.body, initData);
          lastRound = currentRound(result);
          clearPending(sessionStorage, key, saved.body.requestId);
          if (mounted.current) setPending(null);
        } catch (failure) {
          if (failure.status >= 400 && failure.status < 500) {
            clearPending(sessionStorage, key, saved.body.requestId);
            if (mounted.current) setPending(null);
          } else throw failure;
        }
      }
      const state = await penaltyRequest("state", {}, initData);
      applyState(state, lastRound);
    } catch (failure) {
      if (mounted.current) setError(failure.message || "Не удалось восстановить раунд");
    } finally {
      gate.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  React.useEffect(() => {
    mounted.current = true;
    restore();
    return () => { mounted.current = false; };
  }, []);

  async function act(operation, extra = {}) {
    if (gate.current || locked || !initData) return;
    gate.current = true; setBusy(true); setError("");
    const body = { requestId: crypto.randomUUID(), ...extra };
    if (operation !== "start") { body.roundId = round.id; body.goals = round.goals; }
    const saved = { operation, body };
    try {
      // Persist before sending: closing the page never loses the retry ID.
      sessionStorage.setItem(key, JSON.stringify(saved));
      setPending(saved);
      const result = await penaltyRequest(operation, body, initData);
      clearPending(sessionStorage, key, body.requestId);
      if (!mounted.current) return;
      setPending(null);
      setRound(currentRound(result));
      setData(previous => ({ ...previous, balance: result.balance, activeRound: result.activeRound,
        history: result.round.status !== "active" ? [result.round, ...previous.history.filter(item => item.id !== result.round.id)].slice(0, 30) : previous.history }));
      balanceCallback.current(result.balance);
      if (operation === "shoot") setAnimation({ id: body.requestId, direction: result.direction, event: result.event });
    } catch (failure) {
      if (!mounted.current) return;
      if (failure.status >= 400 && failure.status < 500) {
        clearPending(sessionStorage, key, body.requestId); setPending(null);
      }
      setError(failure.message || "Не удалось выполнить действие");
    } finally {
      gate.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  function chooseCurrency(value) {
    setCurrency(value);
    setStake(data.rules.currencies[value].minimum);
  }

  function changeStake(direction) {
    const aligned = direction < 0 ? Math.ceil(stake / rule.step) : Math.floor(stake / rule.step);
    setStake(Math.min(rule.maximum, Math.max(rule.minimum, (aligned + direction) * rule.step)));
  }

  const CurrencyIcon = icons[currency];
  return (
    <section className={`penalty-page penalty-panel-${panel}`} aria-labelledby="penalty-title">
      <header className="penalty-header">
        <h1 id="penalty-title">Пенальти</h1>
        <nav className="penalty-tools" aria-label="Разделы игры">
          <button type="button" title="Правила" aria-label="Правила" aria-haspopup="dialog" aria-expanded={rulesOpen} onClick={() => setRulesOpen(true)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v6m0-10v1" /></svg></button>
          <button type="button" className="penalty-history-tool" title="История" aria-label="История" aria-pressed={panel === "history"} onClick={() => setPanel(panel === "history" ? "game" : "history")}><IconHistory aria-hidden="true" /><span>История</span></button>
        </nav>
      </header>
      {error && <div className="penalty-error" role="alert"><p>{error}</p><button type="button" disabled={busy || !!animation} onClick={restore}>{busy ? "Восстановление…" : "Восстановить раунд"}</button></div>}
      {panel === "game" && <>
        <div ref={multiplierStrip} className="penalty-multipliers" role="region" tabIndex={0} aria-label="Шкала множителей — прокрутите горизонтально">
          {(data?.rules.multipliers || []).map((value, index) => <span key={value} className={round?.goals === index + 1 ? "current" : round?.goals > index + 1 ? "passed" : ""} aria-current={round?.goals === index + 1 ? "step" : undefined}>{mult(value)}</span>)}
        </div>
        <nav className="penalty-keeper-picker" aria-label="Выбор вратаря" style={{ "--keeper-accent": keeper.color }}>
          <button type="button" aria-label="Предыдущий вратарь" disabled={busy || !!animation || !!pending} onClick={() => changeKeeper(-1)}><IconChevronLeft aria-hidden="true" /></button>
          <div aria-live="polite"><strong>{keeper.name}</strong><span>{keeper.series}</span></div>
          <span className="penalty-keeper-count" title={`${keeper.title}. Вратари отличаются внешностью и репликами; шансы гола одинаковы.`}>{keeperIndex + 1}<small> / {KEEPERS.length}</small></span>
          <button type="button" aria-label="Следующий вратарь" disabled={busy || !!animation || !!pending} onClick={() => changeKeeper(1)}><IconChevronRight aria-hidden="true" /></button>
        </nav>
        <PenaltyScene animation={animation} round={round} keeper={keeper.id} accent={keeper.color}>
        <div className="penalty-scene-hud"><span className="penalty-balance" title={rule?.label} aria-label={`${rule?.label || 'Баланс'}: ${data ? fmt(balance) : 'загрузка'}`}><CurrencyIcon aria-hidden="true" />{data ? fmt(balance) : "—"}</span><p className="penalty-keeper-quote" key={quote} aria-live="polite">«{quote}»</p></div>
        <div key={`${round?.id}:${round?.status}`} className={`penalty-status ${round?.payout ? 'penalty-status-paid' : ''}`} role="status" aria-live="polite">
          {busy ? "Сверяемся с сервером…" : animation ? "" : active ? round.goals ? `${goalCount(round.goals)} · ${mult(round.multiplier)}` : "Выберите направление удара" : round ? `${outcomes[round.status]}${round.payout ? `: ${fmt(round.payout)} ${data?.rules.currencies[round.currency].label || ''}` : ""}` : "Выберите ставку и начните раунд"}
        </div>
        </PenaltyScene>
        <div className="penalty-directions"><button type="button" disabled={!active || locked || !!error} onClick={() => act("shoot", { direction: "left" })}>◀ Влево</button><button type="button" disabled={!active || locked || !!error} onClick={() => act("shoot", { direction: "right" })}>Вправо ▶</button></div>
        <div className="penalty-controls">
          <div className="penalty-currencies" role="group" aria-label="Валюта ставки">
            {Object.entries(data?.rules.currencies || {}).map(([id, entry]) => { const Icon = icons[id]; return <button type="button" key={id} title={entry.label} aria-label={entry.label} disabled={locked || active} aria-pressed={currency === id} onClick={() => chooseCurrency(id)}><Icon aria-hidden="true" /></button>; })}
          </div>
          <div className="penalty-stake">
            <button type="button" aria-label="Уменьшить ставку" disabled={locked || active || !rule || stake <= rule.minimum} onClick={() => changeStake(-1)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14" /></svg></button>
            <label title={rule ? `${rule.label} · ${fmt(rule.minimum)}–${fmt(rule.maximum)} · шаг ${fmt(rule.step)}` : 'Ставка'}><CurrencyIcon aria-hidden="true" /><input aria-label={`Ставка${rule ? ` в ${rule.label}, шаг ${rule.step}` : ''}`} type="number" inputMode="numeric" value={stake} min={rule?.minimum} max={rule?.maximum} step={rule?.step} disabled={locked || active} onChange={event => setStake(Number(event.target.value))} /></label>
            <button type="button" aria-label="Увеличить ставку" disabled={locked || active || !rule || stake >= rule.maximum} onClick={() => changeStake(1)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14M12 5v14" /></svg></button>
          </div>
          {!active ? <button type="button" className="penalty-primary" disabled={locked || !!error || !initData || !rule || stake < rule.minimum || stake > rule.maximum || stake % rule.step !== 0 || stake > balance} onClick={() => act("start", { currency, stake })}>{busy ? "Загрузка…" : "Начать раунд"}</button> : <>
            <button type="button" className="penalty-primary penalty-cashout" disabled={locked || !!error || !round.goals} onClick={() => act("cashout")}>{round.goals ? <>Забрать <CurrencyIcon aria-hidden="true" />{fmt(round.cashout)}</> : "Забрать после гола"}</button>
          </>}
          {!active && data && stake > balance && <p className="penalty-insufficient">Недостаточно {currency === "gems" ? "рубинов" : currency === "event" ? "тыкв" : rule?.label}</p>}
        </div>
      </>}
      {rulesOpen && <PenaltyRules rules={data?.rules} onClose={() => setRulesOpen(false)} />}
      {panel === "history" && <PenaltyHistory initData={initData} personalHistory={data?.history || []} />}
    </section>
  );
}
